<script setup lang="ts">
import type { Music, GenMusic } from '~/services/musicService';
import { musicService } from '~/services/musicService';
import { getSectionByPath, generateTitleFromPath, getSectionById } from '~/services/musicSectionMapConfig';
import { useI18n, useRoute, useRouter, ref, onMounted, onUnmounted, watch, useHead, computed } from '#imports';
import { usePagination } from '~/composables/usePagination';
import MusicList from '~/components/music/MusicList.vue';
import { useAuthState } from '~/composables/useAuthState';
import { useToast } from '~/composables/useToast';
import { useMusicCreate } from '~/composables/useMusicCreate';
import { trackEvent } from '~/utils/analytics';

const { user, userAccount, handleSignIn } = useAuthState();
const { saveMusicData } = useMusicCreate();
const toast = useToast();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// 状态
const chartTitle = ref('');
const sectionParam = route.params.section as string;

// 判断URL类型：新的/music/[section]格式，或者旧的查询参数格式
const isLegacyUrl = !sectionParam && (route.query.chart || route.query.category);
const sectionInfo = sectionParam ? getSectionByPath(sectionParam) : null;

// 设置标题
if (sectionParam && !isLegacyUrl) {
  chartTitle.value = sectionInfo?.title || generateTitleFromPath(sectionParam);
} else {
  chartTitle.value = route.query.title as string;
}

// 确定section类型
let sectionType: 'category' | 'rank' = 'category';
if (sectionParam && sectionInfo) {
  sectionType = sectionInfo.type as 'category' | 'rank';
} else if (route.query.chart) {
  sectionType = 'rank';
} else if (route.query.category) {
  sectionType = 'category';
}

// 分页相关状态
const currentPage = ref(parseInt(route.query.page as string) || 1);
const totalPages = ref(1);
const PAGE_SIZE = 20;
const SSR_API_TIMEOUT_MS = 6500;

// --- SSR: 使用 useAsyncData 在服务端获取第一页数据 ---
const { data: ssrResponse } = await useAsyncData(
  `music-section-${sectionParam}-page-${currentPage.value}`,
  async () => {
    if (!sectionParam || isLegacyUrl || !sectionInfo) return null;
    try {
      if (sectionInfo.type === 'rank') {
        return await musicService.getMoreChartMusic(
          sectionInfo.id,
          sectionInfo.selected_option,
          { page: currentPage.value, size: PAGE_SIZE },
          { timeout: SSR_API_TIMEOUT_MS }
        );
      } else {
        return await musicService.getMoreCategoryMusic(sectionParam, {
          page: currentPage.value,
          size: PAGE_SIZE,
        }, { timeout: SSR_API_TIMEOUT_MS });
      }
    } catch (e) {
      console.error('SSR fetch failed:', e);
      return null;
    }
  }
);

// 从 SSR 响应中提取初始数据
const extractInitialData = () => {
  if (!ssrResponse.value) return undefined;
  const response = ssrResponse.value as any;
  let items: Music[] = [];
  let totalCount = 0;

  if (Array.isArray(response.data)) {
    items = response.data;
  } else if (response.data && Array.isArray(response.data.list)) {
    items = response.data.list;
  }

  if (response.data && typeof response.data.total === 'number') {
    totalCount = response.data.total;
  } else if (typeof response.total === 'number') {
    totalCount = response.total;
  }

  if (items.length === 0) return undefined;
  return { list: items, total: totalCount };
};

const ssrInitialData = extractInitialData();

// 用 SSR 数据初始化 totalPages
if (ssrInitialData && ssrInitialData.total > 0) {
  totalPages.value = Math.ceil(ssrInitialData.total / PAGE_SIZE);
}

// 动态生成获取数据的函数
const fetchMusicData = async (pageNum: number) => {
  if (sectionParam && !isLegacyUrl && sectionInfo) {
    if (sectionInfo.type === 'rank') {
      const response = await musicService.getMoreChartMusic(
        sectionInfo.id,
        sectionInfo.selected_option,
        { page: pageNum, size: PAGE_SIZE }
      );
      if (response && response.total) {
        totalPages.value = Math.ceil(response.total / PAGE_SIZE);
      }
      return response;
    } else {
      const response = await musicService.getMoreCategoryMusic(sectionParam, { page: pageNum, size: PAGE_SIZE });
      if (response && response.total) {
        totalPages.value = Math.ceil(response.total / PAGE_SIZE);
      }
      return response;
    }
  } else {
    const chartId = route.query.chart as string;
    const category = route.query.category as string;

    if (chartId) {
      const response = await musicService.getMoreChartMusic(chartId, undefined, { page: pageNum, size: PAGE_SIZE });
      if (response && response.total) {
        totalPages.value = Math.ceil(response.total / PAGE_SIZE);
      }
      return response;
    } else if (category) {
      const response = await musicService.getMoreCategoryMusic(category, { page: pageNum, size: PAGE_SIZE });
      if (response && response.total) {
        totalPages.value = Math.ceil(response.total / PAGE_SIZE);
      }
      return response;
    } else {
      throw new Error(t('music.invalid_params'));
    }
  }
};

// 使用分页hooks，传入 SSR 初始数据避免客户端重复请求
const {
  list: musicList,
  isLoading,
  isLoadingMore,
  error,
  loadMoreTrigger,
  loadData,
  hasMore,
  loadMore
} = usePagination<Music>(
  fetchMusicData,
  {
    pageSize: PAGE_SIZE,
    initialPage: currentPage.value,
    initialData: ssrInitialData,
    infiniteScroll: false,
    errorHandler: (err) => {
      console.error(`加载音乐数据失败:`, err);
      error.value = t('common.load_failed');
    }
  }
);

// SEO配置
const { setMusicSectionSeo } = useSeo();
setMusicSectionSeo({
  title: chartTitle.value,
  type: sectionType,
  path: route.path,
  page: currentPage.value,
});

// SEO intro 文案
const seoIntroText = computed(() => {
  if (sectionType === 'rank') {
    return `Explore trending AI-generated songs from MusicMint. Discover the most popular ${chartTitle.value} tracks created with artificial intelligence.`;
  }
  return `Browse AI-generated ${chartTitle.value} songs and create similar music. Discover unique ${chartTitle.value} tracks made with AI on MusicMint.`;
});

// JSON-LD structured data - 基于 SSR 后的 musicList
const structuredData = computed(() => {
  const baseUrl = 'https://musicmint.ai';
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": chartTitle.value || 'Music Collection',
    "description": t(`meta.musicSection.description_${sectionType}`, { section: chartTitle.value }),
    "url": `${baseUrl}${route.fullPath}`,
    "mainEntity": {
      "@type": "MusicPlaylist",
      "name": chartTitle.value || 'Music Collection',
      "numTracks": musicList.value?.length || 0,
      "track": (musicList.value || []).map((music: Music, index: number) => ({
        "@type": "MusicRecording",
        "position": index + 1,
        "name": music.title || music.metadata?.prompt || '',
        "duration": music.metadata?.duration ? `PT${Math.floor(music.metadata.duration / 60)}M${Math.floor(music.metadata.duration % 60)}S` : undefined,
        "genre": music.metadata?.tags?.split(',').map((tag: string) => tag.trim()) || [],
        "description": music.metadata?.prompt || '',
        "url": `${baseUrl}/music/song/${music.id}`
      }))
    }
  };
});

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: () => JSON.stringify(structuredData.value)
    }
  ]
});

// 监听路由变化，当页码参数变化时重新加载数据
watch(() => route.query.page, (newPage) => {
  const pageNum = parseInt(newPage as string) || 1;
  if (pageNum !== currentPage.value) {
    currentPage.value = pageNum;
    loadData(pageNum);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}, { immediate: false });

// 修改导航函数
const navigateToPage = (page: number) => {
  if (page < 1 || page > totalPages.value) return;

  if (sectionParam) {
    router.push({ path: route.path, query: { page: page.toString() } });
  } else {
    const query = { ...route.query, page: page.toString() };
    router.push({ path: route.path, query });
  }
};

// 推荐分类（用于空结果时展示和底部相关链接）
const recommendedSections = [
  { path: 'pop', title: 'Pop' },
  { path: 'rock', title: 'Rock' },
  { path: 'hip-hop', title: 'Hip Hop' },
  { path: 'electronic', title: 'Electronic' },
  { path: 'jazz', title: 'Jazz' },
  { path: 'r-and-b', title: 'R&B' },
  { path: 'anime', title: 'Anime' },
  { path: 'country', title: 'Country' },
  { path: 'lofi', title: 'Lo-Fi' },
];

// 相关分类（排除当前 section）
const relatedCategories = computed(() =>
  recommendedSections.filter(s => s.path !== sectionParam).slice(0, 6)
);

// Prompt templates 根据 section 生成
const sectionPromptTemplates: Record<string, Array<{ title: string; prompt: string }>> = {
  pop: [
    { title: 'Cheerful Pop Song', prompt: 'A cheerful and catchy pop song with upbeat energy and sing-along chorus' },
    { title: 'Romantic Pop Ballad', prompt: 'A romantic pop ballad with emotional vocals and heartfelt lyrics about love' },
    { title: 'Dance Pop Track', prompt: 'An energetic dance pop track with infectious beat and club-ready production' },
    { title: 'Indie Pop', prompt: 'A dreamy indie pop song with jangly guitars and introspective lyrics' },
    { title: 'Synth Pop', prompt: 'A retro synth-pop track with 80s-inspired synthesizers and catchy melodies' },
  ],
  rock: [
    { title: 'Energetic Rock Song', prompt: 'A powerful rock song with driving guitars, thundering drums, and raw energy' },
    { title: 'Cinematic Rock Intro', prompt: 'An epic cinematic rock intro with building tension and soaring guitar solo' },
    { title: 'Acoustic Rock Ballad', prompt: 'A heartfelt acoustic rock ballad that builds into a full band arrangement' },
    { title: 'Punk Rock', prompt: 'A fast and furious punk rock track with rebellious attitude and three-chord energy' },
    { title: 'Classic Rock Anthem', prompt: 'A classic rock anthem with soaring guitar riffs and fist-pumping chorus' },
  ],
  anime: [
    { title: 'Anime Opening Theme', prompt: 'An energetic anime opening theme with J-Pop vocals and dramatic build-ups' },
    { title: 'Emotional Anime Ending', prompt: 'A beautiful and melancholic anime ending theme with soft piano and strings' },
    { title: 'Battle Anime BGM', prompt: 'An intense battle theme for anime with orchestral power and driving rhythm' },
    { title: 'Slice of Life Theme', prompt: 'A cheerful and warm anime theme for a slice-of-life series' },
    { title: 'Romance Anime Insert', prompt: 'A tender romantic insert song for an anime confession scene' },
  ],
  jazz: [
    { title: 'Smooth Jazz Evening', prompt: 'A smooth jazz track with silky saxophone, electric piano, and gentle drums' },
    { title: 'Late-Night Jazz', prompt: 'A slow and intimate jazz ballad with breathy saxophone and sparse piano' },
    { title: 'Bossa Nova', prompt: 'A breezy bossa nova track with nylon guitar and Brazilian jazz rhythm' },
    { title: 'Jazz Fusion', prompt: 'A funky jazz fusion track blending complex harmonies with electric guitar' },
    { title: 'Jazz Club Trio', prompt: 'A classic jazz trio performance with upright bass, piano, and brushed drums' },
  ],
  country: [
    { title: 'Heartfelt Country Song', prompt: 'A heartfelt country song with acoustic guitar and storytelling lyrics' },
    { title: 'Country Road Trip', prompt: 'An upbeat country song about driving down back roads with windows down' },
    { title: 'Country Love Song', prompt: 'A sweet country love song about finding your person in a small town' },
    { title: 'Modern Country-Pop', prompt: 'A modern country-pop crossover with catchy hooks and Nashville production' },
    { title: 'Outlaw Country', prompt: 'A gritty outlaw country song with rebellious attitude and raw guitar' },
  ],
  lofi: [
    { title: 'Study Lo-Fi Beat', prompt: 'A mellow lo-fi beat perfect for studying with soft piano and vinyl crackle' },
    { title: 'Rainy Day Lo-Fi', prompt: 'A cozy lo-fi track with rain sounds, warm bass, and melancholic piano' },
    { title: 'Coffee Shop Vibes', prompt: 'A warm lo-fi track capturing the atmosphere of a cozy coffee shop' },
    { title: 'Late Night Coding', prompt: 'A minimal lo-fi beat for late-night coding with ambient pads' },
    { title: 'Sunset Lo-Fi', prompt: 'A dreamy nostalgic lo-fi beat with guitar samples and tape saturation' },
  ],
  electronic: [
    { title: 'Festival EDM', prompt: 'A massive festival EDM track with epic build-ups and huge drops' },
    { title: 'Deep House', prompt: 'A smooth deep house track with warm bass and shuffling hi-hats' },
    { title: 'Melodic Techno', prompt: 'A driving melodic techno track with arpeggiated synths and pulsing bass' },
    { title: 'Future Bass', prompt: 'A colorful future bass track with lush chords and bouncy supersaws' },
    { title: 'Ambient Electronic', prompt: 'A spacious ambient electronic piece with evolving textures and pads' },
  ],
};

const promptTemplates = computed(() => {
  const key = sectionParam?.toLowerCase();
  if (sectionPromptTemplates[key]) return sectionPromptTemplates[key];
  return [
    { title: `Create a ${chartTitle.value} Song`, prompt: `A unique ${chartTitle.value} song with creative melodies and engaging rhythm` },
    { title: `Upbeat ${chartTitle.value}`, prompt: `An upbeat and energetic ${chartTitle.value} track with catchy hooks` },
    { title: `Chill ${chartTitle.value}`, prompt: `A relaxed and chill ${chartTitle.value} song perfect for unwinding` },
    { title: `Emotional ${chartTitle.value}`, prompt: `An emotional and heartfelt ${chartTitle.value} piece with deep lyrics` },
    { title: `Modern ${chartTitle.value}`, prompt: `A modern ${chartTitle.value} track with contemporary production style` },
  ];
});

// FAQ
const faqItems = computed(() => [
  {
    question: `What is ${chartTitle.value} AI music?`,
    answer: `${chartTitle.value} AI music is original music generated by artificial intelligence in the ${chartTitle.value} style. MusicMint uses advanced AI models to create unique, high-quality tracks that sound professionally produced.`,
  },
  {
    question: `Can I create a similar ${chartTitle.value} song?`,
    answer: `Yes! Click "Create Similar" on any song or use the prompt templates above to generate your own ${chartTitle.value} music. You can customize the style, mood, and lyrics to make it uniquely yours.`,
  },
  {
    question: `Can I download these ${chartTitle.value} songs?`,
    answer: `Yes, you can download AI-generated songs from MusicMint. Free users get limited downloads, while subscribers have unlimited access to high-quality audio files.`,
  },
  {
    question: `Can I use AI-generated ${chartTitle.value} music commercially?`,
    answer: `Yes! Music generated on MusicMint can be used for personal and commercial projects including YouTube videos, podcasts, social media content, and more.`,
  },
]);

// Create CTA handler
const handleSectionCreate = (prompt?: string) => {
  trackEvent('music_section_create_click', { source: 'section', page_path: route.path, slug: sectionParam, tags: sectionParam });
  saveMusicData({
    prompt: prompt || `A ${chartTitle.value} song`,
    tags: sectionParam || chartTitle.value,
    mode: 'normal',
    source: 'section_page',
    sourcePage: route.path,
  }, route.path);
};

// Page view tracking (client only, once)
onMounted(() => {
  trackEvent('music_section_view', { source: 'section', page_path: route.path, slug: sectionParam, tags: sectionParam });
});
</script>

<template>
  <div class="music-list-page">
    <div class="lg:px-4">
      <!-- 页面标题 + SEO intro -->
      <div class="mb-4 lg:mb-8">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
          {{ chartTitle || t('music.library') }}
        </h1>
        <p class="mt-2 text-gray-600 dark:text-gray-400 text-sm lg:text-base">
          {{ seoIntroText }}
        </p>
      </div>

      <!-- Create CTA -->
      <div class="mb-6">
        <button
          @click="handleSectionCreate()"
          class="inline-flex items-center px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium text-sm hover:from-blue-600 hover:to-purple-700 transition-all shadow-md"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          {{ sectionType === 'rank' ? 'Create a Trending AI Song' : `Create a ${chartTitle} Song` }}
        </button>
      </div>

      <!-- 有数据时正常展示 -->
      <template v-if="musicList.length > 0 || isLoading">
        <MusicList
          :music-list="musicList"
          :is-loading="isLoading"
          :is-loading-more="isLoadingMore"
          :has-more="hasMore"
          :error="error"
          :show-create-similar="true"
          :auto-load-more="false"
          @reload="() => loadData(1)"
          @load-more="loadMore"
        />
      </template>

      <!-- 空结果状态 -->
      <template v-else-if="!isLoading && musicList.length === 0">
        <div class="text-center py-16">
          <div class="mb-6">
            <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
          </div>
          <h2 class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
            No {{ chartTitle }} songs yet
          </h2>
          <p class="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Be the first to create AI-generated {{ chartTitle }} music on MusicMint.
          </p>
          <NuxtLink
            to="/create"
            class="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium hover:from-blue-600 hover:to-purple-700 transition-all"
          >
            Create {{ chartTitle }} Music
          </NuxtLink>

          <!-- 推荐分类 -->
          <div class="mt-10">
            <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">Explore other genres</h3>
            <div class="flex flex-wrap justify-center gap-2">
              <NuxtLink
                v-for="section in recommendedSections"
                :key="section.path"
                :to="`/music/${section.path}`"
                class="px-4 py-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {{ section.title }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </template>

      <!-- 对搜索引擎可见但对用户隐藏的分页导航 -->
      <div aria-hidden="true" class="sr-only">
        <div class="pagination-index">
          <span>{{ t('common.page') }}: {{ currentPage }} / {{ totalPages }}</span>
          <ul>
            <li v-if="currentPage > 1">
              <a :href="`${route.path}?page=1`" @click.prevent="navigateToPage(1)">
                {{ t('common.firstPage') }}
              </a>
            </li>
            <li v-if="currentPage > 1">
              <a :href="`${route.path}?page=${currentPage - 1}`" @click.prevent="navigateToPage(currentPage - 1)">
                {{ t('common.prevPage') }}
              </a>
            </li>
            <li v-for="page in Math.min(10, totalPages)" :key="page">
              <a :href="`${route.path}?page=${page}`" @click.prevent="navigateToPage(page)">
                {{ page }}
              </a>
            </li>
            <li v-if="currentPage < totalPages">
              <a :href="`${route.path}?page=${currentPage + 1}`" @click.prevent="navigateToPage(currentPage + 1)">
                {{ t('common.nextPage') }}
              </a>
            </li>
            <li v-if="currentPage < totalPages">
              <a :href="`${route.path}?page=${totalPages}`" @click.prevent="navigateToPage(totalPages)">
                {{ t('common.lastPage') }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- 可见分页控件 -->
      <div v-if="totalPages > 1" class="flex justify-center items-center mt-8 mb-4 gap-3 flex-wrap">
        <button
          v-if="currentPage > 1"
          @click="navigateToPage(currentPage - 1)"
          class="px-5 py-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-sm font-medium transition-all"
        >
          {{ t('common.prevPage') }}
        </button>

        <div class="flex items-center gap-1.5">
          <input
            ref="pageInput"
            type="number"
            :value="currentPage"
            min="1"
            :max="totalPages"
            class="w-12 h-8 text-center text-sm rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            @keydown.enter="($event) => { const v = parseInt(($event.target as HTMLInputElement).value); if (v >= 1 && v <= totalPages) navigateToPage(v); }"
          />
          <span class="text-gray-400 text-sm">/ {{ totalPages }}</span>
          <button
            class="px-2.5 h-8 rounded-lg bg-gray-700 hover:bg-blue-600 text-white text-xs font-medium transition-colors"
            @click="() => { const v = parseInt(($refs.pageInput as HTMLInputElement)?.value); if (v >= 1 && v <= totalPages) navigateToPage(v); }"
          >Go</button>
        </div>

        <button
          v-if="currentPage < totalPages"
          @click="navigateToPage(currentPage + 1)"
          class="px-5 py-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-sm font-medium transition-all"
        >
          {{ t('common.nextPage') }}
        </button>
      </div>

      <!-- Prompt Templates -->
      <section class="mt-12 mb-10">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {{ chartTitle }} Prompt Ideas
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div
            v-for="(template, idx) in promptTemplates"
            :key="idx"
            class="p-4 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/50 hover:border-blue-500/50 transition-colors cursor-pointer group"
            @click="handleSectionCreate(template.prompt)"
          >
            <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-1 group-hover:text-blue-400 transition-colors">
              {{ template.title }}
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{{ template.prompt }}</p>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="mb-10">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Frequently Asked Questions
        </h2>
        <div class="space-y-3">
          <details
            v-for="(item, idx) in faqItems"
            :key="idx"
            class="group rounded-xl bg-gray-100 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 overflow-hidden"
          >
            <summary class="px-5 py-3 cursor-pointer text-gray-900 dark:text-white text-sm font-medium flex items-center justify-between hover:bg-gray-200 dark:hover:bg-gray-700/30 transition-colors">
              <span>{{ item.question }}</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180 shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div class="px-5 pb-4 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
              {{ item.answer }}
            </div>
          </details>
        </div>
      </section>

      <!-- Related Categories -->
      <section class="mb-8">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Explore More Genres
        </h2>
        <div class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="cat in relatedCategories"
            :key="cat.path"
            :to="`/music/${cat.path}`"
            class="px-4 py-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700/50 hover:border-blue-500/50 transition-all"
          >
            {{ cat.title }}
          </NuxtLink>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.genre-filter {
  scrollbar-width: none;
}

.genre-filter::-webkit-scrollbar {
  display: none;
}

.pagination-index {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
